import type { ShortFileReference } from 'services/file-service';

enum MasstransportTaskType {
  AvailableOnline = 0,
  AvailableOffline = 5,
  Nlocked = 15,
  SplitProject = 20,
  SplitGlobal = 25,
}

export interface DetailedProjectTask {
  description: string;
  descriptionWithNumber: string;
  id: string;
  masstransportTaskType: MasstransportTaskType;
  number: string;
  projId: string;
  comment?: string;
  isParent: boolean;
}

// Keep in sync with ProjectNotificationAssigneeRules.MaxAssignees in the backend (timestyr).
export const MAX_ALERT_ASSIGNEES = 2;

// Make sure it matches Alerts.AlertStatus translation keys
export enum AlertStatus {
  Open,
  InProgress,
  Closed,
}

export enum ProjectsAlertsTabs {
  All = -1,
  Open,
  InProgress,
  Closed,
}

interface AlertOrProjectLocation {
  accuracy: number;
  coordinates: [number, number];
  isValidPoint: boolean;
  properties: unknown;
  type: 'Point';
}

export interface AlertsProject {
  active: boolean;
  inProgressCount: number;
  location: AlertOrProjectLocation;
  openCount: number;
  projectAvatarUrl: string;
  projectId: string;
  projectName: string;
  projectNumber: string;
}

export interface AlertsProjectsResponse {
  currentUserProjectNotificationCount: number;
  projects: AlertsProject[];
}

// Make sure numbers match Alerts.SeverityLevels from translations
export enum SeverityLevel {
  Trivial = -5,
  Low = 0,
  Medium = 5,
  High = 10,
  Critical = 15,
}

// Make sure numbers match Alerts.Report.DamageTypes.0 from translations
export enum InjuryType {
  NoInjury = 0,
  WithoutAbsence = 1,
  WithAbsence = 2,
  InjuryOther = 3,
}

// Make sure numbers match lerts.Report.DamageTypes.3 from translations
export enum EnvironmentalDamageType {
  NoDamage = 0,
  MinorDamage = 1,
  SeriousDamage = 2,
}

// Make sure numbers match Alerts.Report.DamageTypes.2 from translations
export enum QADamageType {
  NoDamage = 0,
  MaterialDamage = 3,
}

// Make sure numbers match Alerts.BaseTypes from translations
export enum CompanyAlertBaseType {
  HSE = 0,
  QA = 2,
  Environmental = 3,
  Machine = 4,
  Other = 1,
}

export interface SelectableOption {
  id: string;
  name: string;
}

export enum CustomPropertyType {
  Dropdown = 0,
  Dataset = 1,
  Text = 2,
}

// Must match Alerts.CompanyTypeSettings.Required
export enum CustomPropertyRequired {
  Optional,
  RequiredWeb,
  RequiredWebAndApp,
}

export type AlertTypeCustomProperty = {
  id: string;
  type: CustomPropertyType;
  name: string;
  datasetId: string | null;
  options: SelectableOption[];
  availableOnApp: boolean;
  required: CustomPropertyRequired;
} & (
  | {
      type: CustomPropertyType.Dropdown | CustomPropertyType.Text;
      datasetId: null;
    }
  | { type: CustomPropertyType.Dataset; datasetId: string }
);

export interface NewSelectableOption {
  id?: string;
  name: string;
}

export type NewAlertTypeCustomProperty = Omit<
  AlertTypeCustomProperty,
  'id' | 'options'
> & {
  id: string | null;
  options: NewSelectableOption[];
};

export interface CompanyAlertType {
  damageInfo: boolean;
  active: boolean;
  availableOnApp: boolean;
  baseType: CompanyAlertBaseType;
  categories: SelectableOption[];
  cause: SelectableOption[];
  companyId: string;
  responsible: string[];
  id: string;
  involvedParties: SelectableOption[];
  linkOnlyAvailable: boolean;
  modifiedAt: string;
  modifiedByUserName: string | null;
  modifiedByUserId: string | null;
  name: string;
  projectId: null | string;
  publishInFeed: boolean;
  imageRequired: boolean;
  risk: boolean;
  sendEmail: boolean;
  exportable: boolean;
  cost: boolean;
  costRequired: boolean;
  hmsRegTypeId: string | null;
  serialPrefix: string | null;
  externalNumber: string | null;
  workRelatedTo: SelectableOption[];
  customProperties: AlertTypeCustomProperty[];
  customCategory: SelectableOption[];
  customCategoryPropertyName: null | string;
  forbiddenBaseTypes: CompanyAlertBaseType[];
}

export interface ProjectAlertType extends CompanyAlertType {
  activeFromCompany?: boolean;
  availableOnAppFromCompany?: boolean;
  companyName: string;
  costFromCompany?: boolean;
  costRequiredFromCompany?: boolean;
  damageInfoFromCompany?: boolean;
  /**
   * ASSUMED CONTRACT (backend pending): ids of company custom properties
   * disabled on this project. Absent/empty = all enabled.
   * Only read in mapProjectAlertTypeToForm — cheap to rename.
   */
  disabledCustomProperties?: string[];
  hasProjectSetup: boolean;
  hmsRegTypeIdFromCompany?: boolean;
  imageRequiredFromCompany?: boolean;
  linkOnlyAvailableFromCompany?: boolean;
  nextSerialNumber: number;
  publishInFeedFromCompany?: boolean;
  riskFromCompany?: boolean;
  sendEmailFromCompany?: boolean;
  serialPrefixFromCompany?: boolean;
}

export interface HMSRegAlertType {
  id: string;
  name: string;
}

export type CompanyAlertTypeKeysWithOptions =
  | 'categories'
  | 'cause'
  | 'involvedParties'
  | 'workRelatedTo';

export enum CompanyTypeState {
  Initial,
  New,
  Deleted,
}

export interface NewProjectAlertType {
  damageInfo?: boolean | null;
  active: boolean | null;
  availableOnApp: boolean | null;
  cost?: boolean | null;
  costRequired?: boolean | null;
  /**
   * ASSUMED CONTRACT (backend pending): null = no project opinion (inherit);
   * [] = all enabled; ids = disabled on this project.
   * Only written in mapFormToNewProjectAlertType — cheap to rename.
   */
  disabledCustomProperties?: string[] | null;
  hmsRegTypeId?: string | null;
  linkOnlyAvailable?: boolean | null;
  modifiedAt?: string | null; // utc z
  modifiedByUserId?: string | null;
  projectId: string | null;
  projectNotificationTypeParentId: string | null;
  publishInFeed: boolean | null;
  imageRequired: boolean | null;
  risk: boolean | null;
  sendEmail?: boolean | null;
  responsible?: string[] | null;
  serialPrefix?: string | null;
  nextSerialNumber: number | null;
}

export interface AlertUserParam {
  avatarImageId: string;
  companyId: string;
  companyName: string;
  companyOrgNumber?: string;
  employeeNumber?: string;
  id: string;
  name: string;
  mobilePhone: string;
}

export enum AlertHistoryChangeType {
  Created = 0,
  Edited = 10,
  Status = 20,
  StatusAndEdit = 22,
  Assigned = 30,
  AssignedAndEdit = 32,
}

export interface AlertHistoryItem {
  userId: string;
  userName: string;
  changeDate: string;
  change: AlertHistoryChangeType;
  assignedUserId?: string;
  assignedUserName?: string;
  status?: AlertStatus;
}

interface AlertCompany {
  address: string;
  avatarSrc: string;
  companyId: string;
  contactPerson: string;
  countryCode: string;
  id: string;
  logoImageFileRef57: string;
  logoImageFileRef72: string;
  logoImageFileRef114: string;
  logoImageFileRef144: string;
  logoImageFileRefOrig: string;
  name: string;
  orgNr: string;
  phoneNumber: string;
  timeZoneId: string;
}

export interface AlertCategory {
  id: string;
  name: string;
}

export enum ProjectNotificationAccessLevel {
  NoAccess = 0,
  Read = 1,
  Write = 2,
}

export interface AlertCustomProperty {
  propertyId: string;
  propertyName: string;
  textValue: string | null;
  dropdownIds: string[];
  datasetItemId: string | null;
  datasetItemExternalId: string | null;
  datasetItemName: string | null;
  datasetItemNumber: string | null;
  datasetData: object | null;
  dropdownOptions: SelectableOption[];
}

interface CommonAlertFields {
  absenceDays?: number;
  // Legacy single assignee (first of assignedToUsers) — kept for backward compatibility.
  assignedTo?: AlertUserParam;
  assignedToId?: string;
  assignedToUsers?: AlertUserParam[];
  assignedToIds?: string[];
  baseType: CompanyAlertBaseType;
  caseHandlerDescriptionMeasures: string | null;
  categories: AlertCategory[];
  categoryId: string | null;
  categoryIds: string[] | null;
  categoryName: string;
  causes?: SelectableOption[];
  causeId?: string;
  causeIds?: string[];
  causeName: string;
  commentInternal?: string;
  companyId: string;
  companyIds: string[];
  company: AlertCompany;
  connectedChecklistId: string | null;
  costs: number | null;
  invoicedCost: number | null;
  internalCost: number | null;
  currentUserAccessLevel: ProjectNotificationAccessLevel;
  customCategories?: SelectableOption[];
  customCategoryId?: string;
  customCategoryIds?: string[];
  customProperties?: AlertCustomProperty[];
  createdAt: string; // utc z
  createdBy: AlertUserParam;
  createdByUserId: string;
  description: string;
  exportable: boolean;
  externalId?: string;
  hmsRegIntegrationEnabled: boolean;
  hmsRegTypeSetOnAlertType: boolean;
  history?: AlertHistoryItem[];
  hmsRegTypeId: string | null;
  id: string;
  imageFileReferences: ShortFileReference[];
  imageIds: string[];
  fileFileReferences: ShortFileReference[];
  fileIds: string[];
  duplicatedFromId?: string;
  involvedCompanyIds: string[] | null;
  involvedCompanies: AlertCompany[];
  involvedParties?: SelectableOption[];
  involvedPartiesId?: string;
  involvedPartiesIds: string[];
  involvedPartiesName: string;
  involvedResources?: string;
  latitude?: number;
  locationPoint?: AlertOrProjectLocation;
  longitude?: number;
  machineName: string | null;
  machineResourceNumber: string | null;
  measures: string;
  modifiedAt: string; // utc z
  modifiedByUserId: string;
  motherCompanyName: string | null;
  projectCompanyId: string;
  projectCompanyName: string;
  projectId: string;
  projectName?: string;
  projectNumber: string;
  projectNotificationTypeId: string;
  readyForExportToExternal: boolean;
  recVersion: number;
  relatedTo?: SelectableOption[];
  relatedToId?: string;
  relatedToIds?: string[];
  relatedToName: string;
  requiresFurtherAction: boolean;
  customCategoryName?: string;
  customCategoryPropertyName?: string;
  resolvedOnLocation: boolean;
  risk: SeverityLevel;
  serialNumber: string;
  serialNumberPrefix: string | null;
  serialNumberWithPrefix: string;
  projectSerialNumber: string;
  projectSerialNumberFull: string;
  /** Array of emails */
  sentTo: string[];
  sentToUserIds: string[] | null;
  sentToUsers:
    | {
        avatarImageId: string | null;
        companyId: string | null;
        companyName: string | null;
        companyOrgNumber: string | null;
        email: string;
        employeeNumber: string | null;
        id: string | null;
        mobilePhone: string | null;
        name: string | null;
      }[]
    | null;
  status: AlertStatus;
  showDamageInfo: boolean;
  showRisk: boolean;
  showCost: boolean;
  costRequired: boolean;
  taskId: string;
  taskName: string;
  taskNumber: string;
  title: string;
  typeName: string;
  onlyResetMetaAndStatus?: boolean;
}

export type CompanyAlert = (
  | {
      baseType?: CompanyAlertBaseType.HSE;
      damageType?: InjuryType;
      machineId: null;
    }
  | {
      baseType?: CompanyAlertBaseType.Environmental;
      damageType?: EnvironmentalDamageType;
      machineId: null;
    }
  | {
      baseType?: CompanyAlertBaseType.QA;
      damageType?: QADamageType;
      machineId: null;
    }
  | {
      baseType?: CompanyAlertBaseType.Machine;
      damageType?: 0;
      machineId: string;
    }
  | {
      baseType?: CompanyAlertBaseType.Other;
      damageType?: 0;
      machineId: null;
    }
) &
  CommonAlertFields;

export type MachineAlert = {
  baseType?: CompanyAlertBaseType.Machine;
  damageType?: 0;
  machineId: string;
} & CommonAlertFields;

export interface AssignedDetails {
  notificationIds: string[];
  assignedToIds: string[];
}

export interface CompanySettings {
  id: string;
  companyId: string;
  recVersion: number;
  companyIds: string[];
  receiversOfAllChecklists: string[];
  receiversOfAllNotifications: string[];
}

export interface ProjectSettings extends CompanySettings {
  projectId: string;
}

export interface AlertsPDFGenerationPusherPayload {
  projectNotificationId: string;
}
